import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useUserStore } from '@/stores/user';

let nextId = 1;

export const useNotificationStore = defineStore('notification', () => {

    const notifications = ref([]);
    const userStore = useUserStore();

    function getPrefs() {
        // Defensive: ensure structure exists
        return userStore?.preferences?.notifications || {
            autoDismissSuccess: true,
            autoDismissDelayMs: 4000,
            maxVisible: 5,
            playSound: false,
            soundVolume: 0.4
        };
    }

    function playNotificationSound(type) {
        const prefs = getPrefs();
        if (!prefs.playSound || typeof window === 'undefined') return;
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const duration = 0.18;
            const baseFreq = type === 'error' ? 320 : type === 'warning' ? 480 : 680;
            osc.frequency.value = baseFreq;
            gain.gain.value = prefs.soundVolume ?? 0.4;
            osc.connect(gain); gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + duration);
            // Ramp down to avoid click
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
        } catch (e) { /* ignore sound errors */ }
    }

    function scheduleAutoDismiss(notification) {
        const prefs = getPrefs();
        if (notification.type === 'success' && prefs.autoDismissSuccess) {
            const delay = Math.max(500, prefs.autoDismissDelayMs || 4000);
            setTimeout(() => {
                // ensure still present
                const idx = notifications.value.findIndex(n => n.id === notification.id);
                if (idx !== -1) removeNotification(notification.id);
            }, delay);
        }
    }

    // function addNotification(message, type = 'error', identifier = null) {
    //     const id = nextId++;
    //     const scssClass = type;
    //     notifications.value.push({ id, message, type, class: scssClass, visible: true, identifier });
    // }

    // function addNotification(message, type = 'error', identifier = null) {
    //     // identifier = z.B. 1 für Activity, 2 für Performance, ...
    //     // Prüfe, ob schon eine Notification mit dieser identifier existiert
    //     const existing = notifications.value.find(n => n.identifier === identifier && n.message === message && n.type === type);
    //     if (existing) {
    //         existing.count = (existing.count || 1) + 1;
    //         // Sichtbar machen, falls sie schon ausgeblendet war
    //         existing.visible = true;
    //     } else {
    //         const id = identifier ?? nextId++;
    //         const scssClass = type;
    //         notifications.value.push({ id, message, type, class: scssClass, visible: true, identifier, count: 1 });
    //     }
    // }

    function addNotification(message, type = 'error', identifier = null) {
        const now = new Date();
        const timeString = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const existing = notifications.value.find(n => n.identifier === identifier && n.message === message && n.type === type);
        if (existing) {
            existing.count = (existing.count || 1) + 1;
            existing.times = existing.times || [];
            existing.times.push(timeString);
            existing.visible = true;
        } else {
            const id = identifier ?? nextId++;
            const scssClass = type;
            const notification = {
                id,
                message,
                type,
                class: scssClass,
                visible: true,
                showTimes: false,
                identifier,
                count: 1,
                times: [timeString]
            };
            notifications.value.push(notification);
            scheduleAutoDismiss(notification);
            playNotificationSound(type);
        }

        // Enforce maxVisible preference (keep newest, remove oldest extras)
        const prefs = getPrefs();
        const maxVisible = Math.max(1, prefs.maxVisible || 5);
        if (notifications.value.length > maxVisible) {
            // remove oldest beyond limit
            while (notifications.value.length > maxVisible) {
                notifications.value.shift();
            }
        }
    }

    function removeNotification(id) {
        const notificationIndex = notifications.value.findIndex(n => n.id === id);
        if (notificationIndex !== -1) {
            const notification = notifications.value[notificationIndex];
            notification.visible = false;
            notifications.value.splice(notificationIndex, 1);
            return notification.identifier;
        }
        return null;
    }

    function copyNotification(id, event = null) {
        const notification = notifications.value.find(n => n.id === id);
        if (notification) {
            navigator.clipboard.writeText(notification.message)
                .then(() => {
                    console.log('Notification copied to clipboard:', notification.message);
    
                    // Feedback anzeigen, wenn ein Event (Button) übergeben wurde
                    if (event) {
                        const button = event.target.closest('button');
                        if (button) {
                            const feedback = document.createElement('span');
                            feedback.textContent = 'Kopiert!';
                            feedback.className = 'copy-feedback success';
                            button.appendChild(feedback); // direkt an den Button anhängen
                    
                            setTimeout(() => {
                                feedback.remove();
                            }, 5000);
                        }
                    }
                })
                .catch(err => {
                    console.error('Failed to copy notification:', err);
    
                    // Fehler-Feedback anzeigen, wenn ein Event (Button) übergeben wurde
                    if (event) {
                        const button = event.target.closest('button');
                        if (button) {
                            const feedback = document.createElement('span');
                            feedback.textContent = 'Kopieren fehlgeschlagen!';
                            feedback.className = 'copy-feedback failure';
                            button.parentElement.appendChild(feedback);
    
                            setTimeout(() => {
                                feedback.remove();
                            }, 2000);
                        }
                    }
                });
        }
    }

    function clearNotifications() {
        notifications.value = [];
    }

    // function getCssClass(type) {
    //     switch (type) {
    //         case 'success':
    //             return '#6dd17d'; 
    //         case 'warning':
    //             return '#f7c863';
    //         case 'error':
    //         default:
    //             return '#f8d7da';
    //     }
    // }

    return { notifications, addNotification, removeNotification, clearNotifications, copyNotification };
});
